import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Input, Label } from '@/components/ui';
import type { Money } from '@/lib/api-admin/types';
import {
  settlementPayoutSchema,
  type SettlementPayoutValues,
} from '../schemas/settlementFormSchema';

const formatMoney = (money: Money | undefined | null): string => {
  if (!money) return '—';
  return `${new Intl.NumberFormat('ko-KR').format(money.amount)} ${money.currencyCode}`;
};

export interface PayoutCreateModalProps {
  isOpen: boolean;
  runId: string;
  vendor: string;
  netPayout: Money;
  canExecute: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onConfirm: (input: { bankAccountId?: string; note?: string }) => void;
  onCancel: () => void;
}

export const PayoutCreateModal = ({
  isOpen,
  runId,
  vendor,
  netPayout,
  canExecute,
  isSubmitting,
  errorMessage,
  onConfirm,
  onCancel,
}: PayoutCreateModalProps) => {
  const { t } = useTranslation('settlement');
  const form = useForm<SettlementPayoutValues>({
    resolver: zodResolver(settlementPayoutSchema),
    defaultValues: { bankAccountId: '', note: '' },
    mode: 'onBlur',
  });

  const submit = (values: SettlementPayoutValues) => {
    const input: { bankAccountId?: string; note?: string } = {};
    if (values.bankAccountId && values.bankAccountId.length > 0) {
      input.bankAccountId = values.bankAccountId;
    }
    if (values.note && values.note.length > 0) {
      input.note = values.note;
    }
    onConfirm(input);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o && !isSubmitting) onCancel(); }}>
      <DialogContent data-testid="payout-create-modal" className="max-w-md">
        <DialogTitle>{t('settlement.payout.title')}</DialogTitle>
        <DialogDescription>
          {t('settlement.payout.subject', {
            runId,
            vendor,
            amount: formatMoney(netPayout),
          })}
        </DialogDescription>

        {!canExecute ? (
          <p
            role="alert"
            data-testid="payout-permission-denied"
            className="rounded-md p-3 text-sm"
            style={{
              background: 'var(--status-danger-bg)',
              color: 'var(--status-danger-fg)',
            }}
          >
            {t('settlement.payout.permissionDenied')}
          </p>
        ) : (
          <p
            className="rounded-md p-3 text-sm"
            style={{
              background: 'var(--status-warn-bg)',
              color: 'var(--status-warn-fg)',
            }}
          >
            {t('settlement.payout.warning')}
          </p>
        )}

        <form
          onSubmit={form.handleSubmit(submit)}
          className="flex flex-col gap-2"
          noValidate
        >
          <Label htmlFor="bank-account-id">{t('settlement.payout.bankAccountLabel')}</Label>
          <Input
            id="bank-account-id"
            {...form.register('bankAccountId')}
            disabled={!canExecute}
            placeholder="(blank = vendor primary)"
            style={{ height: 'var(--density-compact-form-h)' }}
          />
          <span className="text-[10px] text-[color:var(--content-muted)]">
            {t('settlement.payout.bankAccountHint')}
          </span>

          <Label htmlFor="payout-note" className="mt-2">
            {t('settlement.payout.noteLabel')}
          </Label>
          <textarea
            id="payout-note"
            {...form.register('note')}
            disabled={!canExecute}
            maxLength={500}
            rows={3}
            className="w-full rounded-md border px-3 py-2 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2"
            style={{ borderColor: 'var(--content-muted)' }}
          />

          {errorMessage ? (
            <p
              role="alert"
              className="rounded-md p-2 text-sm"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
            >
              {errorMessage}
            </p>
          ) : null}

          <div className="mt-3 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('settlement.actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant="cta"
              size="md"
              disabled={!canExecute || isSubmitting}
            >
              {isSubmitting
                ? t('settlement.payout.submitting')
                : t('settlement.payout.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
